// Continuous loops use native scrolling, avoiding large transformed text layers on Safari.
(() => {
  const motion = matchMedia('(prefers-reduced-motion: reduce)');
  const marquees = new Map();
  const belts = new Map();
  let keyboard = false;
  document.addEventListener('keydown', () => { keyboard = true; }, true);
  document.addEventListener('pointerdown', () => { keyboard = false; }, true);
  document.addEventListener('touchstart', () => { keyboard = false; }, { passive: true, capture: true });

  function marquee(view) {
    const track = view.querySelector('.marquee-track');
    if (!track?.firstElementChild || marquees.has(view)) return;
    const original = track.firstElementChild.cloneNode(true);
    original.removeAttribute('aria-hidden');
    original.querySelectorAll('a').forEach(link => link.removeAttribute('tabindex'));
    const durationText = track.style.animationDuration || getComputedStyle(track).animationDuration;
    const duration = Math.max(1, parseFloat(durationText) || 22) * (durationText.endsWith('ms') ? .001 : 1);
    let cycle = 0, position = 0, last = 0, frame = 0, queued = 0, width = -1;
    let hovered = false, resumeAt = 0;
    const pointers = new Set(), cleanup = [];
    const listen = (node, type, fn, options) => {
      node.addEventListener(type, fn, options); cleanup.push(() => node.removeEventListener(type, fn, options));
    };
    const hideRepeat = node => {
      node.setAttribute('aria-hidden', 'true');
      node.querySelectorAll('a').forEach(link => link.tabIndex = -1);
      return node;
    };
    listen(view, 'pointerenter', event => { if (event.pointerType === 'mouse') hovered = true; });
    listen(view, 'pointerleave', () => { hovered = false; });
    listen(view, 'pointerdown', event => { pointers.add(event.pointerId); }, { passive: true });
    const release = event => { if (pointers.delete(event.pointerId)) resumeAt = performance.now() + 250; };
    listen(window, 'pointerup', release, { passive: true });
    listen(window, 'pointercancel', release, { passive: true });
    listen(view, 'lostpointercapture', release, { passive: true });
    if (!window.PointerEvent) {
      listen(view, 'touchstart', () => { pointers.add('touch'); }, { passive: true });
      const end = () => { pointers.clear(); resumeAt = performance.now() + 250; };
      listen(window, 'touchend', end, { passive: true });
      listen(window, 'touchcancel', end, { passive: true });
    }
    listen(window, 'blur', () => { pointers.clear(); hovered = false; });
    track.dataset.loopReady = 'true';
    function fit(force = false) {
      const nextWidth = view.clientWidth;
      if (!view.isConnected || (!force && nextWidth === width)) return;
      width = nextWidth;
      const progress = cycle ? position / cycle : 0;
      const focused = [...track.querySelectorAll('a:not([tabindex="-1"])')].indexOf(document.activeElement);
      const half = document.createElement('span');
      half.className = 'marquee-set';
      half.append(original.cloneNode(true));
      track.replaceChildren(half);
      view.scrollLeft = 0;
      const restoreFocus = () => {
        const link = focused >= 0 ? half.querySelectorAll('a:not([tabindex="-1"])')[focused] : null;
        if (link) { link.focus({ preventScroll: true }); link.scrollIntoView({ block: 'nearest', inline: 'nearest' }); position = view.scrollLeft; }
      };
      if (motion.matches || !width) { cycle = 0; position = 0; restoreFocus(); return; }
      const unitWidth = half.getBoundingClientRect().width;
      if (!unitWidth) { cycle = 0; position = 0; restoreFocus(); return; }
      const copies = Math.max(1, Math.ceil((width + 1) / unitWidth));
      for (let i = 1; i < copies; i++) half.append(hideRepeat(original.cloneNode(true)));
      track.append(hideRepeat(half.cloneNode(true)));
      cycle = half.getBoundingClientRect().width;
      position = progress * cycle;
      view.scrollLeft = position;
      restoreFocus();
    }
    const queue = () => { cancelAnimationFrame(queued); queued = requestAnimationFrame(() => fit()); };
    const fontFit = () => fit(true);
    const observer = window.ResizeObserver ? new ResizeObserver(queue) : null;
    observer?.observe(view);
    window.addEventListener('resize', queue);
    motion.addEventListener('change', fontFit);
    document.fonts?.ready.then(() => { if (view.isConnected) fontFit(); });
    function tick(now) {
      if (!view.isConnected) { destroy(); return; }
      const delta = last ? Math.min(50, now - last) : 0;
      last = now;
      const focusedByKeyboard = keyboard && view.contains(document.activeElement);
      const paused = hovered || pointers.size || focusedByKeyboard || now < resumeAt;
      if (cycle > 0 && !document.hidden && !motion.matches && !paused) {
        position = (position + delta * cycle / (duration * 1000)) % cycle;
        view.scrollLeft = position;
      } else if (cycle > 0) position = Math.max(0, view.scrollLeft);
      frame = requestAnimationFrame(tick);
    }
    function destroy() {
      cancelAnimationFrame(frame); cancelAnimationFrame(queued); observer?.disconnect();
      cleanup.forEach(remove => remove());
      window.removeEventListener('resize', queue); motion.removeEventListener('change', fontFit);
      marquees.delete(view);
    }
    marquees.set(view, { destroy }); fit(true); frame = requestAnimationFrame(tick);
  }

  function belt(rail) {
    if (belts.has(rail)) return;
    rail.querySelectorAll('[data-belt-copy]').forEach(node => node.remove());
    const originals = [...rail.children];
    if (originals.length < 2) return;
    let cycle = 0, firstCopy = null, position = rail.scrollLeft, last = 0, frame = 0;
    let hovered = false, pauseUntil = 0, written = rail.scrollLeft;
    const pointers = new Set(), cleanup = [];
    const listen = (node, type, fn, options) => {
      node.addEventListener(type, fn, options); cleanup.push(() => node.removeEventListener(type, fn, options));
    };
    const pause = (delay = 180) => {
      pauseUntil = performance.now() + delay; position = Math.max(0, rail.scrollLeft);
    };
    const release = event => { if (pointers.delete(event.pointerId)) pause(); };
    listen(rail, 'pointerdown', event => { pointers.add(event.pointerId); pause(); }, { passive: true });
    listen(window, 'pointerup', release, { passive: true });
    listen(window, 'pointercancel', release, { passive: true });
    listen(rail, 'lostpointercapture', release, { passive: true });
    if (!window.PointerEvent) {
      listen(rail, 'touchstart', () => { pointers.add('touch'); pause(); }, { passive: true });
      const end = () => { pointers.clear(); pause(); };
      listen(window, 'touchend', end, { passive: true }); listen(window, 'touchcancel', end, { passive: true });
    }
    listen(rail, 'pointerenter', event => { if (event.pointerType === 'mouse') hovered = true; });
    listen(rail, 'pointerleave', () => { hovered = false; });
    listen(window, 'blur', () => { pointers.clear(); hovered = false; });
    listen(rail, 'wheel', () => pause(250), { passive: true });
    listen(rail, 'keydown', () => pause(250));
    // Native touch momentum can continue briefly after pointerup. Let it finish.
    listen(rail, 'scroll', () => { if (Math.abs(rail.scrollLeft - written) > 1) pause(); }, { passive: true });
    rail.closest('.more-products')?.querySelectorAll('[data-rail-step]').forEach(button => {
      listen(button, 'click', () => {
        pause(450);
        rail.scrollBy({ left: Number(button.dataset.railStep) * rail.clientWidth * .85, behavior: motion.matches ? 'instant' : 'smooth' });
      });
    });
    function appendSet() {
      originals.forEach(card => {
        const copy = card.cloneNode(true);
        // Copies must not inherit a temporary add-in-progress state.
        copy.querySelectorAll('form[data-busy]').forEach(form => {
          delete form.dataset.busy; form.removeAttribute('aria-busy');
        });
        copy.querySelectorAll('[data-cart-disabled]').forEach(control => {
          control.disabled = control.dataset.cartDisabled === 'true';
          delete control.dataset.cartDisabled;
        });
        copy.querySelectorAll('.quick-status').forEach(status => { status.textContent = ''; });
        copy.dataset.beltCopy = 'true';
        // Repeats remain tappable; keyboard and screen readers visit the originals once.
        copy.setAttribute('aria-hidden', 'true');
        copy.querySelectorAll('a,button,input,summary,[tabindex]').forEach(node => node.tabIndex = -1);
        copy.querySelectorAll('[id]').forEach(node => node.removeAttribute('id'));
        rail.append(copy); if (!firstCopy) firstCopy = copy;
      });
    }
    function measure() {
      if (!rail.isConnected || motion.matches || Number(rail.dataset.conveyorSpeed) === 0 || !rail.clientWidth) return;
      if (!firstCopy) appendSet();
      cycle = firstCopy.offsetLeft - originals[0].offsetLeft;
      if (cycle <= 0) return;
      let count = 0;
      while (rail.scrollWidth < cycle + rail.clientWidth + 2 && count++ < 12) appendSet();
    }
    const observer = window.ResizeObserver ? new ResizeObserver(measure) : null;
    observer?.observe(rail);
    listen(window, 'resize', measure); listen(motion, 'change', measure);
    function tick(now) {
      if (!rail.isConnected) { destroy(); return; }
      const delta = last ? Math.min(50, now - last) : 0; last = now;
      const focusedByKeyboard = keyboard && rail.contains(document.activeElement);
      const paused = pointers.size || hovered || focusedByKeyboard || rail.querySelector('details[open],form[data-busy]') || now < pauseUntil;
      if (cycle > 0 && !motion.matches && !document.hidden && !paused) {
        const speed = Math.max(0, Math.min(80, Number(rail.dataset.conveyorSpeed ?? 25)));
        position = (position + delta * speed / 1000) % cycle;
        rail.scrollLeft = position; written = rail.scrollLeft;
      } else { position = Math.max(0, rail.scrollLeft); written = rail.scrollLeft; }
      frame = requestAnimationFrame(tick);
    }
    function destroy() {
      cancelAnimationFrame(frame); observer?.disconnect(); cleanup.forEach(remove => remove());
      rail.querySelectorAll('[data-belt-copy]').forEach(node => node.remove()); belts.delete(rail);
    }
    belts.set(rail, { destroy }); measure(); frame = requestAnimationFrame(tick);
  }
  function init(scope = document) {
    scope.querySelectorAll('.marquee-link').forEach(marquee);
    scope.querySelectorAll('[data-conveyor]').forEach(belt);
  }
  window.initPocketConveyors = init;
  window.resetPocketConveyors = (scope = document) => {
    scope.querySelectorAll('[data-conveyor]').forEach(rail => belts.get(rail)?.destroy()); init(scope);
  };
  document.addEventListener('DOMContentLoaded', () => init());
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    [marquees, belts].forEach(map => map.forEach((controller, node) => { if (event.target.contains(node)) controller.destroy(); }));
  });
})();
